import React, { useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import healthcareAnimation from '../../assets/HealthCare.lottie';

interface DashboardLoaderProps {
  message?: string;
  size?: number;
  minHeight?: number | string;
  inline?: boolean;
  sx?: SxProps<Theme>;
}

/** DotLottie needs WASM + IntersectionObserver; skip it on very old Safari / iOS. */
function canUseDotLottie(): boolean {
  if (typeof window === 'undefined') return false;
  return typeof (window as any).WebAssembly === 'object' && typeof IntersectionObserver === 'function';
}

const DashboardLoader: React.FC<DashboardLoaderProps> = ({
  message,
  size = 220,
  minHeight = 220,
  inline = false,
  sx,
}) => {
  const [useLottie, setUseLottie] = useState(canUseDotLottie);
  const spinnerSize = Math.min(Math.max(size * 0.35, 28), 64);

  return (
    <Box
      sx={{
        display: inline ? 'inline-flex' : 'flex',
        flexDirection: inline ? 'row' : 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: inline ? 'auto' : minHeight,
        width: inline ? 'auto' : '100%',
        lineHeight: 0,
        verticalAlign: 'middle',
        ...sx,
      }}
    >
      {useLottie ? (
        <Box
          onErrorCapture={() => setUseLottie(false)}
          sx={{ width: size, height: size, maxWidth: '100%', flexShrink: 0 }}
        >
          <DotLottieReact
            src={healthcareAnimation}
            loop
            autoplay
            renderConfig={{ freezeOnOffscreen: false }}
            style={{
              width: size,
              height: size,
              maxWidth: '100%',
              flexShrink: 0,
            }}
          />
        </Box>
      ) : (
        <CircularProgress size={spinnerSize} thickness={4} sx={{ flexShrink: 0 }} />
      )}
      {message ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
          {message}
        </Typography>
      ) : null}
    </Box>
  );
};

export default DashboardLoader;
